/**
 * Utility functions for managing user data in localStorage
 * These functions ensure consistency across the application when updating user data
 */

const STORAGE_KEY = "doctolikLoginInfo";

/**
 * Get user data from localStorage
 * @returns {Object|null} User data object or null if not found
 */
export const getUserFromLocalStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error reading user data from localStorage:", error);
    return null;
  }
};

/**
 * Save complete user data to localStorage
 * @param {Object} userData - Complete user data object
 */
export const saveUserToLocalStorage = (userData) => {
  try {
    if (userData) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    }
  } catch (error) {
    console.error("Error saving user data to localStorage:", error);
  }
};

/**
 * Update specific fields in user data (supports nested paths)
 * @param {Object} updates - Object containing the fields to update
 * @param {Function} setUserData - Optional React setState function to update context
 * @returns {Object|null} Updated user data or null if error
 * 
 * @example
 * // Update top-level field
 * updateUserInLocalStorage({ name: "John Doe" })
 * 
 * // Update nested field
 * updateUserInLocalStorage({ "users.profile_pic": "new-pic.jpg" })
 * 
 * // Update multiple fields with context update
 * updateUserInLocalStorage({ 
 *   "users.profile_pic": "pic.jpg",
 *   "users.speciality": "Cardiology" 
 * }, setUserData)
 */
export const updateUserInLocalStorage = (updates, setUserData = null) => {
  try {
    const currentData = getUserFromLocalStorage();
    if (!currentData) {
      console.warn("No user data found in localStorage");
      return null;
    }

    // Create a deep clone to avoid mutations
    const updatedData = JSON.parse(JSON.stringify(currentData));

    // Apply updates
    Object.entries(updates).forEach(([key, value]) => {
      // Support nested paths like "users.profile_pic"
      const keys = key.split('.');
      let target = updatedData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!target[keys[i]]) {
          target[keys[i]] = {};
        }
        target = target[keys[i]];
      }
      
      target[keys[keys.length - 1]] = value;
    });

    // Save to localStorage
    saveUserToLocalStorage(updatedData);

    // Update context if setter provided
    if (setUserData && typeof setUserData === 'function') {
      setUserData(updatedData);
    }

    return updatedData;
  } catch (error) {
    console.error("Error updating user data in localStorage:", error);
    return null;
  }
};

/**
 * Update user's profile information after API response
 * @param {Object} apiResponse - API response data containing user info
 * @param {Function} setUserData - React setState function to update context
 * @returns {Object|null} Updated user data or null if error
 */
export const updateUserFromApiResponse = (apiResponse, setUserData = null) => {
  try {
    const currentData = getUserFromLocalStorage();
    if (!currentData || !apiResponse) {
      return null;
    }

    const updatedData = JSON.parse(JSON.stringify(currentData));
    
    // Update user fields if they exist in the response
    if (apiResponse.users) {
      updatedData.users = {
        ...updatedData.users,
        ...apiResponse.users
      };
    }

    // Save to localStorage
    saveUserToLocalStorage(updatedData);

    // Update context if setter provided
    if (setUserData && typeof setUserData === 'function') {
      setUserData(updatedData);
    }

    return updatedData;
  } catch (error) {
    console.error("Error updating user from API response:", error);
    return null;
  }
};

/**
 * Remove user data from localStorage
 */
export const removeUserFromLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error removing user data from localStorage:", error);
  }
};

/**
 * Check if user is logged in
 * @returns {boolean}
 */
export const isUserLoggedIn = () => {
  return getUserFromLocalStorage() !== null;
};
